import React, { useId } from 'react';
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";

import type { PaginationProps } from '../../types/componentType';

export type { PaginationProps };

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemLabel = 'itens',
  className = '',
  maxVisiblePages = 5,
  alwaysShow = false
}) => {
  const paginationId = useId();
  const pageInfoId = useId();

  const handlePageChange = (page: number) => {
    // Validate page number
    if (page < 1 || page > totalPages) {
      return;
    }
    onPageChange(page);
  };

  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  // Don't render if there's only one page or no pages (unless alwaysShow is true)
  if (!alwaysShow && totalPages <= 1) {
    return null;
  }

  // Generate page numbers with ellipsis
  const generatePageNumbers = (): React.ReactElement[] => {
    const pages: React.ReactElement[] = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className={`flex items-center rounded-full font-medium transition-all duration-200 cursor-pointer ${1 === currentPage
            ? 'bg-secondary-600 text-white'
            : 'text-gray-700'
            }`}
          aria-label={1 === currentPage ? 'Página atual: 1' : 'Ir para página 1'}
          aria-current={1 === currentPage ? 'page' : undefined}
        >
          <span className="px-3 py-2">1</span>
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="ellipsis1" className="px-2" aria-hidden="true">
            ...
          </span>
        );
      }
    }

    // Add visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`flex items-center rounded-full font-medium transition-all duration-200 cursor-pointer ${i === currentPage
            ? 'bg-secondary-600 text-white'
            : 'text-gray-700'
            }`}
          aria-label={i === currentPage ? `Página atual: ${i}` : `Ir para página ${i}`}
          aria-current={i === currentPage ? 'page' : undefined}
        >
          <span className="px-4 py-2">{i}</span>
        </button>
      );
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="ellipsis2" className="px-2 text-gray-500" aria-hidden="true">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={`flex items-center rounded-full font-medium transition-all duration-200 cursor-pointer ${totalPages === currentPage
            ? 'bg-secondary-600 text-white'
            : 'text-gray-700'
            }`}
          aria-label={totalPages === currentPage ? `Página atual: ${totalPages}` : `Ir para página ${totalPages}`}
          aria-current={totalPages === currentPage ? 'page' : undefined}
        >
          <span className="px-4 py-2">{totalPages}</span>
        </button>
      );
    }

    return pages;
  };

  return (
    <nav
      id={paginationId}
      className={className}
      role="navigation"
      aria-label="Navegação de páginas"
    >
      <div className="flex justify-center items-center gap-8">
        {/* Previous button */}
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={!hasPreviousPage}
          className={`flex items-center rounded-full font-medium transition-all duration-200 cursor-pointer ${hasPreviousPage
            ? 'bg-gray-300 text-white'
            : 'bg-gray-100/60 text-white cursor-pointer'
            }`}
          aria-label={hasPreviousPage ? `Ir para página anterior (página ${currentPage - 1})` : 'Página anterior (indisponível)'}
          aria-disabled={!hasPreviousPage}
        >
          <RiArrowLeftSLine className="w-4 h-4" aria-hidden="true" />
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-1" role="list" aria-label="Números de página">
          {generatePageNumbers()}
        </div>

        {/* Next button */}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={!hasNextPage}
          className={`flex items-center rounded-full font-medium transition-all duration-200 cursor-pointer ${hasNextPage
            ? 'bg-gray-300 text-white'
            : 'bg-gray-100/60 text-white cursor-not-allowed'
            }`}
          aria-label={hasNextPage ? `Ir para próxima página (página ${currentPage + 1})` : 'Próxima página (indisponível)'}
          aria-disabled={!hasNextPage}
        >
          <RiArrowRightSLine className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      {/* Page info */}
      <div
        id={pageInfoId}
        className="text-center mt-2"
        role="status"
        aria-live="polite"
        aria-label="Informações da paginação"
      >
        <span className="text-sm text-gray-600">
          Página {Number(currentPage) || 1} de {Number(totalPages) || 0}
          {totalItems !== undefined && ` • ${Number(totalItems) || 0} ${itemLabel}`}
        </span>
      </div>
    </nav>
  );
};

export default Pagination;

